// frontend/src/components/noel/SantaPremiumPage.jsx
import React, { useEffect, useState } from "react";
import {
  Gift,
  Star,
  Crown,
  Sparkles,
  CheckCircle2,
  ArrowRight,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import "./SantaPremiumPage.css";
import apiClient from "../../Api/apiConfig";
import { useAuth } from "../../lib/auth"; // ✅ IMPORTANT : on utilise le même système que le reste du site

const PRICES = {
  family: 7.9,
  reindeer: 39.9,
};

export default function SantaPremiumPage() {
  const navigate = useNavigate();

  // 🔐 Auth centralisée : comme dans le Navigation
  const { isAuthenticated, user } = useAuth();

  const [currentPlan, setCurrentPlan] = useState(null); // "free" | "family" | "reindeer" | null
  const [loadingPlan, setLoadingPlan] = useState(false);

  // ⬇️ Récupérer le plan actuel de l'utilisateur (si connecté)
  useEffect(() => {
    if (!isAuthenticated) {
      setCurrentPlan(null);
      return;
    }

    const fetchCurrentPlan = async () => {
      try {
        setLoadingPlan(true);
        // 👉 À adapter selon ta route backend
        // Exemple de réponse attendue: { planId: "free" | "family" | "reindeer" }
        const { data } = await apiClient.get("/santa/plan/current");
        setCurrentPlan(data?.planId || data?.plan || null);
      } catch (error) {
        console.error("Erreur récupération plan Père Noël :", error);
        setCurrentPlan(null);
      } finally {
        setLoadingPlan(false);
      }
    };

    fetchCurrentPlan();
  }, [isAuthenticated]);

  const getPlanPath = (planId) => {
    if (planId === "free") return "/pere-noel";
    if (planId === "family") return "/pere-noel/premium/famille-magique";
    if (planId === "reindeer") return "/pere-noel/premium/renne-du-pere-noel";
    return "/pere-noel";
  };

  // 💶 Calcul du prix à payer selon le plan actuel et le plan cible
  const getEffectivePrice = (targetPlanId) => {
    if (targetPlanId === "family") {
      // Pour l’instant, pas de réduction "free → family"
      return PRICES.family;
    }

    if (targetPlanId === "reindeer") {
      if (currentPlan === "family") {
        // Upgrade Famille Magique → Renne du Père Noël
        return Math.max(PRICES.reindeer - PRICES.family, 0);
      }
      // Free → Renne (ou aucun plan connu)
      return PRICES.reindeer;
    }

    return 0;
  };

  // 🧭 Gestion clic sur un plan
  const handleSelect = (planId) => {
    const targetPath = getPlanPath(planId);

    // Plan gratuit toujours accessible
    if (planId === "free") {
      navigate(targetPath);
      return;
    }

    // Si non connecté et plan payant → direction page de connexion
    if (!isAuthenticated) {
      navigate("/login", {
        state: { redirectTo: targetPath },
      });
      return;
    }

    // Si l'utilisateur a déjà le plan le plus haut (Renne) → pas de changement possible
    if (currentPlan === "reindeer" && planId !== "reindeer") {
      alert(
        "Vous bénéficiez déjà du pack Renne du Père Noël, le plus complet. Il n'est pas possible de changer de plan."
      );
      return;
    }

    // Si l’utilisateur clique sur le plan qu’il a déjà
    if (currentPlan === planId) {
      // On le laisse accéder directement à la page correspondante
      navigate(targetPath);
      return;
    }

    // Gestion upgrade Famille Magique → Renne du Père Noël
    const isUpgradeToReindeer =
      currentPlan === "family" && planId === "reindeer";

    const effectivePrice = getEffectivePrice(planId);

    navigate(targetPath, {
      state: {
        selectedPlan: planId,
        currentPlan: currentPlan,
        isUpgrade: isUpgradeToReindeer,
        effectivePrice, // montant que tu pourras utiliser côté paiement
      },
    });
  };

  const formatPrice = (amount) =>
    `${amount.toFixed(2).replace(".", ",")} €`;

  const renderCurrentPlanLabel = (planId) => {
    if (!isAuthenticated || !currentPlan || currentPlan !== planId) return null;
    return <div className="plan-current-badge">Votre plan actuel</div>;
  };

  const getCurrentPlanLabel = () => {
    if (!isAuthenticated) return "Vous n'êtes pas connecté.";
    if (!currentPlan) return "Aucun plan activé pour le moment";
    if (currentPlan === "free") return "Découverte magique (gratuit)";
    if (currentPlan === "family") return "Famille Magique";
    if (currentPlan === "reindeer") return "Renne du Père Noël";
    return "Plan inconnu";
  };

  const reindeerEffectivePrice = getEffectivePrice("reindeer");
  const isUpgradeToReindeer =
    isAuthenticated && currentPlan === "family";

  return (
    <div className="premium-page">
      {/* Bandeau top */}
      <div className="premium-hero">
        <div className="premium-hero-content">
          <div className="premium-hero-label">
            <Sparkles size={16} /> Spécial Noël jusqu&apos;au 25 décembre
          </div>
          <h1>
            Offrez une vraie rencontre magique
            <span> avec le Père Noël</span>
          </h1>
          <p className="premium-hero-text">
            Le chat gratuit, c&apos;est pour tester. Les plans Famille Magique
            et Renne du Père Noël transforment la discussion en un souvenir
            unique : chanson personnalisée, photo-montage, lettre officielle et
            même vidéo générée par IA.
          </p>

          <div className="premium-hero-actions">
            <button
              type="button"
              className="btn-outline"
              onClick={() => handleSelect("free")}
            >
              Commencer gratuitement
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={() => handleSelect("family")}
            >
              Je choisis Famille Magique
              <ArrowRight size={18} />
            </button>
          </div>

          <p className="premium-hero-note">
            Paiement sécurisé – service proposé par l&apos;association
            Nouvelles Méthodes Solidaires 06 (NMS06).
          </p>
        </div>

        <div className="premium-hero-illu">
          <img
            src="/images/perenoel.png"
            alt="Père Noël"
            className="premium-santa-illu"
          />
        </div>
      </div>

      {/* 🔔 Info plan actuel */}
      {(
        // on affiche le bandeau même si pas connecté, avec message différent
        <div className="premium-current-plan-banner">
          {loadingPlan && isAuthenticated ? (
            <span>Chargement de votre plan en cours...</span>
          ) : (
            <>
              <span>Votre plan actuel : </span>
              <strong>{getCurrentPlanLabel()}</strong>
            </>
          )}
        </div>
      )}

      {/* ⭐ Phrase de soutien juste au-dessus des packs */}
      <div className="premium-adhesion-info">
        <p>
          En choisissant un pack payant, vous soutenez directement les actions
          de l&apos;association NMS06. L&apos;adhésion annuelle à
          l&apos;association (valeur 20€) vous est offerte, quel que soit le
          forfait Famille Magique ou Renne du Père Noël choisi.
        </p>
      </div>

      {/* Cartes offres */}
      <section className="premium-plans">
        {/* Plan gratuit */}
        <article className="plan-card plan-free">
          {renderCurrentPlanLabel("free")}

          <div className="plan-header">
            <div className="plan-icon">
              <Gift size={26} />
            </div>
            <h2>Découverte magique</h2>
            <p className="plan-subtitle">Pour tester avec votre enfant</p>
          </div>

          <div className="plan-price">
            <span className="price-main">0 €</span>
            <span className="price-tag">Gratuit</span>
          </div>

          <ul className="plan-features">
            <li>
              <CheckCircle2 size={18} /> 5 messages avec le Père Noël
            </li>
            <li>
              <CheckCircle2 size={18} /> Réponses avec la voix du Père Noël
            </li>
            <li>
              <CheckCircle2 size={18} /> Accès au chat jusqu&apos;au 25
              décembre
            </li>
            <li className="plan-feature-muted">
              ❌ Pas de lettre personnalisée
            </li>
            <li className="plan-feature-muted">
              ❌ Pas de photo-montage ni chanson
            </li>
            <li className="plan-feature-muted">
              ❌ Pas de vidéo personnalisée
            </li>
          </ul>

          <button
            type="button"
            className="btn-outline full-width"
            onClick={() => handleSelect("free")}
          >
            Utiliser la version gratuite
          </button>
        </article>

        {/* Plan Famille Magique */}
        <article className="plan-card plan-family">
          {renderCurrentPlanLabel("family")}

          <div className="plan-header">
            <div className="plan-icon plan-icon-gold">
              <Star size={26} />
            </div>
            <h2>Famille Magique</h2>
            <p className="plan-subtitle">
              Le pack complet pour un souvenir unique
            </p>
          </div>

          <div className="plan-price">
            <span className="price-main">{formatPrice(PRICES.family)}</span>
            <span className="price-tag">Une fois, pour Noël</span>
          </div>

          <ul className="plan-features">
            <li>
              <CheckCircle2 size={18} /> Messages illimités jusqu&apos;au 25
              décembre
            </li>
            <li>
              <CheckCircle2 size={18} /> Lettre personnalisée envoyée au Père
              Noël
            </li>
            <li>
              <CheckCircle2 size={18} /> Réponse du Père Noël par e-mail (PDF)
            </li>
            <li>
              <CheckCircle2 size={18} /> Photo-montage de votre enfant avec le
              Père Noël
            </li>
            <li>
              <CheckCircle2 size={18} /> Chanson personnalisée avec le prénom
              de l&apos;enfant
            </li>
            <li>
              <CheckCircle2 size={18} /> Accès à la voix du Père Noël
            </li>
          </ul>

          <button
            type="button"
            className="btn-primary full-width"
            onClick={() => handleSelect("family")}
          >
            Choisir Famille Magique
            <ArrowRight size={18} />
          </button>

          <p className="plan-footnote">
            Idéal pour un enfant ou une fratrie, géré par les parents.
          </p>
        </article>

        {/* Plan Renne du Père Noël */}
        <article className="plan-card plan-reindeer">
          {renderCurrentPlanLabel("reindeer")}

          <div className="plan-ribbon plan-ribbon-highlight">
            🎁 Carte Cadeau 15€ offerte
          </div>

          <div className="plan-header">
            <div className="plan-icon plan-icon-purple">
              <Crown size={26} />
            </div>
            <h2>Renne du Père Noël</h2>
            <p className="plan-subtitle">
              L&apos;expérience ultime et un cadeau immédiat
            </p>
          </div>

          <div className="plan-price">
            <span className="price-main">
              {isUpgradeToReindeer
                ? formatPrice(reindeerEffectivePrice)
                : formatPrice(PRICES.reindeer)}
            </span>
            <span className="price-tag">
              {isUpgradeToReindeer
                ? "au lieu de 39,90 € (vous avez déjà Famille Magique)"
                : "Pack Cadeau & Vidéo"}
            </span>
            {isUpgradeToReindeer && (
              <div className="plan-upgrade-note">
                Vous ne payez que la différence avec votre pack actuel.
              </div>
            )}
          </div>

          <ul className="plan-features">
            <li>
              <CheckCircle2 size={18} />{" "}
              <strong>Carte cadeau multi-enseigne d&apos;une valeur de 15€.</strong>
            </li>
            <li>
              <CheckCircle2 size={18} />{" "}
              <strong>Tout le plan Famille Magique</strong>
            </li>
            <li>
              <CheckCircle2 size={18} /> Vidéo personnalisée de ~3 minutes du
              Père Noël parlant à l&apos;enfant
            </li>
            <li>
              <CheckCircle2 size={18} /> Messages illimités jusqu&apos;au 25
              décembre
            </li>
            <li>
              <CheckCircle2 size={18} /> Contenus livrés par e-mail (lien
              sécurisé)
            </li>
          </ul>

          <button
            type="button"
            className="btn-secondary full-width"
            onClick={() => handleSelect("reindeer")}
          >
            Choisir Renne du Père Noël
            <ArrowRight size={18} />
          </button>

          <p className="plan-footnote">
            Recommandé pour un cadeau vraiment inoubliable (anniversaire +
            Noël).
          </p>
        </article>
      </section>

      {/* Section rassurante parents */}
      <section className="premium-info">
        <h2>
          <Sparkles size={20} /> Informations pour les parents
        </h2>
        <div className="premium-info-grid">
          <div className="premium-info-item">
            <h3>Données des enfants protégées</h3>
            <p>
              Le prénom de l&apos;enfant est utilisé uniquement pour personnaliser
              la lettre, la chanson et la vidéo. Aucune donnée n&apos;est revendue
              ou utilisée à d&apos;autres fins.
            </p>
          </div>
          <div className="premium-info-item">
            <h3>Paiement sécurisé</h3>
            <p>
              Les paiements sont traités via un prestataire sécurisé (ex. PayPal).
              Vous ne payez qu&apos;une seule fois, sans abonnement caché.
            </p>
          </div>
          <div className="premium-info-item">
            <h3>Association loi 1901</h3>
            <p>
              Ce service est proposé par l&apos;association Nouvelles Méthodes
              Solidaires 06. Les bénéfices aident à financer des actions
              solidaires pour les familles.
            </p>
          </div>
        </div>
      </section>

      {/* CTA final */}
      <section className="premium-bottom-cta">
        <h2>Prêt à faire briller les yeux de votre enfant ?</h2>
        <p>
          Choisissez votre formule, remplissez quelques informations, et on
          s&apos;occupe du reste depuis le Pôle Nord.
        </p>
        <div className="premium-hero-actions">
          <button
            type="button"
            className="btn-outline"
            onClick={() => handleSelect("free")}
          >
            Tester le chat gratuit
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={() => handleSelect("family")}
          >
            Passer en Famille Magique
            <ArrowRight size={18} />
          </button>
        </div>
      </section>
    </div>
  );
}
